import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useTranslation from "../../../../../../packages/locales/translations/util/useTranslation";
import { useParams } from "react-router";
import { useArticleById } from "@coworker/app/src/components/RoomSettings/hooks/useArticleById";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import {
  ArticleListItemMobile,
  SimpleContainer,
} from "@coworker/app/src/components/RoomSettings/styles/styles";
import Trans from "../../../../../../packages/locales/translations/util/Trans";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { MediaPlacementSectionSearch } from "../../../components/MediaPlacementSection/MediaPlacementSectionSearch/MediaPlacementSectionSearch";
import { AboutArticleSection } from "../../../components/AboutArticleSection/AboutArticleSection";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import React, { useState } from "react";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import { ItemListViewItemCard } from "../../../components/item-cards/ItemListViewItemCard/ItemListViewItemCard";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { ArticleViewSkeleton } from "@coworker/app/src/components/RoomSettings/Skeletons/ArticleViewSkeleton";
import { MediaItemType } from "../../../types/mediaItemType";
import { MediaItemViewType } from "../../../types/mediaItemViewType";
import { MediaTasksSectionForSearch } from "../../../components/MediaTasksSection/MediaTasksSectionForSearch";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { MediaPlacementsModal } from "../../../components/MediaPlacementSection/MediaPlacementsModal/MediaPlacementsModal";
export const MediaArticleInformationPageSearch = () => {
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { productArticleId } = useParams();
  const storeId = useStoreId();

  const {
    data: mediasAndArticleCount,
    isLoading: isMediasAndArticleCountLoading,
  } = useMediasAndArticleCount(productArticleId, storeId);

  const { data: searchedArticle, isLoading: isArticleLoading } = useArticleById(
    mediasAndArticleCount?.[0]?.articleId
  );

  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, productArticleId || "");
  const { data: itemInfo, isLoading: isItemInfoLoading } = useItemInfo(
    productArticleId ?? ""
  );

  const { data: taskTypes } = useAllTaskTypes();

  function getTaskTypeId(taskTypeName: TaskTypeNames) {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const totalQuantityInPieces = mediasAndArticleCount?.reduce(
    (acc, media) => acc + media.nbrArticles,
    0
  );
  const hasMediasLoadError =
    !isMediasAndArticleCountLoading && mediasAndArticleCount == null;

  const mediasAndArticleCountNotPartOfLC = mediasAndArticleCount?.filter(
    (media) => !media.parentArticleCombinationNumber
  );

  const canCreateTask =
    hasMediasLoadError ||
    (mediasAndArticleCountNotPartOfLC &&
      mediasAndArticleCountNotPartOfLC.length > 0);

  const taskTypeId = getTaskTypeId(
    TaskTypeNames.MEDIA_MAINTENANCE_MULTIPLE_MEDIA
  );

  const itemCardContent = React.useMemo(() => {
    if (!searchedArticle) return null;
    return (
      <ItemListViewItemCard<Article>
        edsData={edsData?.[0] || null}
        mediaItemType={MediaItemType.REGULAR_ITEM}
        showActions={false}
        itemCardRenderType={ItemCardRenderType.static}
        isItemInGroups={false}
        badges={[]}
        article={searchedArticle}
        mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
        itemInfo={itemInfo ?? null}
        itemInfoLoading={isItemInfoLoading}
        shouldDisplayQuantity={true}
        totalQuantityPieces={totalQuantityInPieces || 0}
      />
    );
  }, [
    searchedArticle,
    edsData,
    itemInfo,
    isItemInfoLoading,
    totalQuantityInPieces,
  ]);

  const linkbar = React.useMemo(() => {
    return (
      <Linkbar
        items={[
          {
            label: t("mediaMaintenanceString"),
            to: "/mediamaintenance",
          },
        ]}
        currentLabel={searchedArticle?.itemName ?? ""}
        currentAsH1
      />
    );
  }, [searchedArticle?.itemName, t]);

  if (!isMediasAndArticleCountLoading && !isArticleLoading && !searchedArticle)
    return (
      <>
        {linkbar}
        <SimpleContainer
          $direction={"column"}
          onClick={() => {
            navigate("/mediamaintenance");
          }}
        >
          <Trans values={{ article: productArticleId }}>
            articleDoesNotExistInRoomSettingString
          </Trans>
          <FixaButton
            type={"primary"}
            text={t("goBackToRoomListingString")}
            onClick={() => {
              navigate("/mediamaintenance");
            }}
          />
        </SimpleContainer>
      </>
    );

  if (!searchedArticle && (isMediasAndArticleCountLoading || isArticleLoading))
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!searchedArticle) return null;

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      actionBarContent={
        canCreateTask && (
          <FixaButton
            type="primary"
            fluid
            text={t("createString")}
            onClick={() => setShowCreateTaskModal(true)}
            disabled={!taskTypeId}
          />
        )
      }
    >
      <FixaListView id="searched-article-list-view" size="small">
        <ArticleListItemMobile>{itemCardContent}</ArticleListItemMobile>
        {isMediasAndArticleCountLoading ? (
          <ArticleViewSkeleton />
        ) : (
          <>
            <MediaPlacementSectionSearch
              itemCardContent={itemCardContent}
              mediasAndArticleCount={mediasAndArticleCount || []}
            />
            <MediaTasksSectionForSearch itemNo={productArticleId ?? ""} />
          </>
        )}
        <AboutArticleSection
          hfb={searchedArticle.hfb}
          itemNoAndType={{
            no: searchedArticle.productArticleId ?? "",
            type: searchedArticle.productArticleType ?? "",
          }}
        />
      </FixaListView>
      {canCreateTask && (
        <MediaPlacementsModal
          itemNoAndType={{
            no: searchedArticle.productArticleId ?? "",
            type: searchedArticle.productArticleType ?? "",
          }}
          showModal={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          mediasAndArticleCount={mediasAndArticleCount ?? []}
          title={t("createTaskString")}
          showCheckBox={true}
          showFooter={true}
          taskTypeId={taskTypeId}
        />
      )}
    </FullScreenPopup>
  );
};
